import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, MessageCircle, Trash2, CheckCircle2, SendHorizonal, FileText, PauseCircle } from "lucide-react";
import { Campanha } from "@/types/campanha";
import { listarCampanhas, deletarCampanha } from "@/lib/campanhas";
import NovaCampanhaModal from "@/components/admin/whatsapp/NovaCampanhaModal";

const statusConfig: Record<string, { label: string; cls: string; icon: any }> = {
  rascunho: { label: "Rascunho", cls: "bg-black/5 text-black/40", icon: FileText },
  enviando: { label: "Enviando", cls: "bg-blue-50 text-blue-600", icon: SendHorizonal },
  pausado:  { label: "Pausado",  cls: "bg-orange-50 text-orange-600", icon: PauseCircle },
  concluido:{ label: "Concluído",cls: "bg-green-50 text-green-700", icon: CheckCircle2 },
};

export default function AdminWhatsappMarketing() {
  const navigate = useNavigate();
  const [campanhas, setCampanhas] = useState<Campanha[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [deletando, setDeletando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setCampanhas(await listarCampanhas()); }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleDelete = async (c: Campanha, e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = window.confirm(`Excluir campanha "${c.nome}"?`);
    if (!ok) return;
    setDeletando(c.id);
    try { await deletarCampanha(c.id); await carregar(); }
    finally { setDeletando(null); }
  };

  return (
    <>
      <div className="p-8 space-y-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-black">WhatsApp Marketing</h1>
            <p className="text-sm text-black/40">
              {campanhas.length > 0
                ? `${campanhas.length} campanha${campanhas.length !== 1 ? "s" : ""}`
                : "Gerencie campanhas de disparo via WhatsApp."}
            </p>
          </div>
          <button
            onClick={() => setModalAberto(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition"
          >
            <Plus className="w-4 h-4" />
            Nova Campanha
          </button>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-5 h-5 animate-spin text-black/20" />
          </div>
        ) : campanhas.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/5 p-16 flex flex-col items-center gap-3 text-center">
            <div className="w-12 h-12 rounded-xl bg-black/5 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-black/20" />
            </div>
            <p className="text-sm font-medium text-black/50">Nenhuma campanha criada.</p>
            <p className="text-xs text-black/30">Clique em &ldquo;Nova Campanha&rdquo; para começar.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {campanhas.map((c) => {
              const s = statusConfig[c.status] ?? statusConfig.rascunho;
              const StatusIcon = s.icon;
              const progresso = c.totalContatos > 0
                ? Math.round(((c.enviados + c.falhas) / c.totalContatos) * 100)
                : 0;

              return (
                <div
                  key={c.id}
                  onClick={() => navigate(`/admin/whatsapp-marketing/${c.id}`)}
                  className="bg-white rounded-2xl border border-black/5 p-5 hover:border-black/10 hover:shadow-sm transition cursor-pointer group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center shrink-0">
                      <MessageCircle className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-black">{c.nome}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${s.cls}`}>
                          <StatusIcon className="w-3 h-3" />
                          {s.label}
                        </span>
                      </div>
                      {c.totalContatos > 0 && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-black/40">{progresso}% concluído</span>
                            <span className="text-black/40">{c.enviados + c.falhas}/{c.totalContatos}</span>
                          </div>
                          <div className="w-full bg-black/5 rounded-full h-1.5">
                            <div className="bg-black h-full rounded-full transition-all" style={{ width: `${progresso}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition">
                      <button
                        onClick={(e) => handleDelete(c, e)}
                        disabled={deletando === c.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-red-500 hover:bg-red-50 transition"
                      >
                        {deletando === c.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalAberto && (
        <NovaCampanhaModal
          onClose={() => setModalAberto(false)}
          onCriada={() => { setModalAberto(false); carregar(); }}
        />
      )}
    </>
  );
}
